import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PackageSearch, ArrowUpRight, Filter, Search } from "lucide-react";
import RFQDialog from "@/components/RFQDialog";
import { useSiteLanguage } from "@/contexts/LanguageContext";
import { pickLanguage } from "@/lib/publicLanguage";
import {
  usePublicCategories,
  usePublicProducts,
  usePublicSettings,
} from "@/hooks/usePublicSiteData";

export default function Products() {
  const [params, setParams] = useSearchParams();
  const active = params.get("category") || "all";
  const [query, setQuery] = useState("");
  const [rfqOpen, setRfqOpen] = useState(false);
  const [rfqProduct, setRfqProduct] = useState(null);
  const { language } = useSiteLanguage();

  const { data: categories = [] } = usePublicCategories();
  const { data: settings } = usePublicSettings();
  const { data: products = [], isLoading } = usePublicProducts(
    ["public-products", active],
    active === "all" ? "/products" : `/products?category=${active}`
  );

  const filtered = products.filter((p) =>
    p.name.toLowerCase().includes(query.toLowerCase()) ||
    p.short_description?.toLowerCase().includes(query.toLowerCase())
  );
  const categoryMap = new Map(categories.map((category) => [category.slug, category]));
  const activeCategory = active === "all" ? null : categoryMap.get(active) || null;
  const showProductGrid = active !== "all" || query.trim().length > 0;
  const copy = {
    badge: pickLanguage(language, {
      en: settings?.products_badge || "B2B Catalog",
      "zh-CN": "B2B 产品目录",
      bn: "B2B পণ্যের ক্যাটালগ",
    }),
    title: pickLanguage(language, {
      en: settings?.products_title || "Sourced. Verified.",
      "zh-CN": "严选采购，严格核验。",
      bn: "সংগ্রহিত। যাচাইকৃত।",
    }),
    highlight: pickLanguage(language, {
      en: settings?.products_highlight || "Ready to ship.",
      "zh-CN": "随时准备发运。",
      bn: "প্রেরণের জন্য প্রস্তুত।",
    }),
    subtitle: pickLanguage(language, {
      en:
        settings?.products_subtitle ||
        "Every product is sourced through audited suppliers with documented MOQ, HS code, and FOB pricing. Request a quote on any product and we'll respond within 24 business hours.",
      "zh-CN":
        "每款产品都来自经过审核的供应商，并附有 MOQ、HS 编码和 FOB 价格等信息。您可以对任意产品提交询盘，我们会在 24 个工作小时内回复。",
      bn:
        "প্রতিটি পণ্য নিরীক্ষিত সরবরাহকারীর কাছ থেকে সংগ্রহ করা হয় এবং MOQ, HS কোড ও FOB মূল্যের নথিভুক্ত তথ্যসহ উপস্থাপিত হয়। যেকোনো পণ্যের জন্য কোটেশন চাইলে আমরা ২৪ কর্মঘন্টার মধ্যে উত্তর দেব।",
    }),
    searchPlaceholder: pickLanguage(language, {
      en: settings?.products_search_placeholder || "Search products, HS code, origin...",
      "zh-CN": "搜索产品、HS 编码、产地……",
      bn: "পণ্য, HS কোড, উৎস দেশ খুঁজুন...",
    }),
    filterLabel: pickLanguage(language, {
      en: "Filter",
      "zh-CN": "筛选",
      bn: "ফিল্টার",
    }),
    all: pickLanguage(language, {
      en: "All",
      "zh-CN": "全部",
      bn: "সব",
    }),
    activeCategoryDescription: pickLanguage(language, {
      en: "Products in this category are managed from the admin panel and shown here automatically.",
      "zh-CN": "该分类下的产品由后台统一管理，并会自动展示在这里。",
      bn: "এই ক্যাটাগরির পণ্যগুলো অ্যাডমিন প্যানেল থেকে পরিচালিত হয় এবং এখানে স্বয়ংক্রিয়ভাবে প্রদর্শিত হয়।",
    }),
    categoryPrompt: pickLanguage(language, {
      en: "Select a category to view its products.",
      "zh-CN": "请选择一个分类以查看对应产品。",
      bn: "পণ্য দেখতে একটি ক্যাটাগরি নির্বাচন করুন।",
    }),
    loading: pickLanguage(language, {
      en: "Loading catalog…",
      "zh-CN": "正在加载目录……",
      bn: "ক্যাটালগ লোড হচ্ছে…",
    }),
    empty: pickLanguage(language, {
      en: settings?.products_empty_text || "No products match your filters. Try clearing search or another category.",
      "zh-CN": "没有符合当前筛选条件的产品。请尝试清除搜索词或切换其他分类。",
      bn: "আপনার ফিল্টারের সাথে মিলে এমন কোনো পণ্য পাওয়া যায়নি। সার্চ পরিষ্কার করুন বা অন্য ক্যাটাগরি চেষ্টা করুন।",
    }),
    moq: pickLanguage(language, {
      en: "MOQ",
      "zh-CN": "起订量",
      bn: "সর্বনিম্ন অর্ডার",
    }),
    price: pickLanguage(language, {
      en: "Price",
      "zh-CN": "价格",
      bn: "মূল্য",
    }),
    onQuote: pickLanguage(language, {
      en: "On Quote",
      "zh-CN": "询价获取",
      bn: "কোটেশন সাপেক্ষে",
    }),
    requestQuote: pickLanguage(language, {
      en: "Request Quote",
      "zh-CN": "申请报价",
      bn: "কোটেশন অনুরোধ করুন",
    }),
  };

  const setCategory = (slug) => {
    if (slug === "all") {
      params.delete("category");
    } else {
      params.set("category", slug);
    }
    setParams(params, { replace: true });
  };

  return (
    <>
      {/* Header */}
      <section className="bg-[var(--rsi-bg-subtle)] py-16" data-testid="products-header">
        <div className="container-rsi">
          <span className="badge-purple">
            <PackageSearch className="h-3.5 w-3.5" /> {copy.badge}
          </span>
          <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter mt-3 text-[var(--rsi-navy)] leading-[1.02]">
            {copy.title}<br />
            <span className="text-[var(--rsi-purple)]">{copy.highlight}</span>
          </h1>
          <p className="mt-4 text-gray-600 max-w-2xl">
            {copy.subtitle}
          </p>

          <div className="mt-10 flex flex-col lg:flex-row gap-4 items-stretch lg:items-center">
            <div className="relative flex-1 max-w-md">
              <Search className="h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={copy.searchPlaceholder}
                data-testid="products-search"
                className="w-full pl-10 pr-4 py-3 rounded-full border border-gray-300 bg-white focus:outline-none focus:ring-2 focus:ring-[var(--rsi-purple)]/30 focus:border-[var(--rsi-purple)] text-sm"
              />
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-gray-500 uppercase tracking-wider mr-1 flex items-center gap-1">
                <Filter className="h-3.5 w-3.5" /> {copy.filterLabel}
              </span>
              <button
                onClick={() => setCategory("all")}
                data-testid="category-filter-all"
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  active === "all"
                    ? "bg-[var(--rsi-purple)] text-white"
                    : "bg-white border border-gray-200 text-gray-700 hover:border-[var(--rsi-purple)]"
                }`}
              >
                {copy.all}
              </button>
              {categories.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCategory(c.slug)}
                  data-testid={`category-filter-${c.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    active === c.slug
                      ? "bg-[var(--rsi-purple)] text-white"
                      : "bg-white border border-gray-200 text-gray-700 hover:border-[var(--rsi-purple)]"
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          {activeCategory && (
            <div className="mt-8 rsi-card overflow-hidden" data-testid={`selected-category-${activeCategory.slug}`}>
              <div className="grid md:grid-cols-[260px_minmax(0,1fr)]">
                <div className="aspect-[5/3] md:aspect-auto bg-gray-100 overflow-hidden">
                  <img src={activeCategory.image_url} alt={activeCategory.name} className="h-full w-full object-cover" />
                </div>
                <div className="p-6 md:p-8">
                  <div className="badge-navy w-fit">{activeCategory.name}</div>
                  <div className="text-xs text-gray-500 font-mono-plex mt-3">/{activeCategory.slug}</div>
                  <h2 className="font-display text-3xl font-black tracking-tight text-[var(--rsi-navy)] mt-2">
                    {activeCategory.name}
                  </h2>
                  <p className="text-gray-600 mt-3 max-w-2xl">
                    {activeCategory.description || copy.activeCategoryDescription}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Grid */}
      <section className="py-16 bg-white" data-testid="products-grid">
        <div className="container-rsi">
          {active === "all" && categories.length > 0 && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12" data-testid="products-category-grid">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setCategory(category.slug)}
                  className="rsi-card overflow-hidden text-left group"
                  data-testid={`products-category-card-${category.slug}`}
                >
                  <div className="aspect-[5/3] bg-gray-100 overflow-hidden">
                    <img
                      src={category.image_url}
                      alt={category.name}
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  </div>
                  <div className="p-5">
                    <div className="font-display font-extrabold text-lg tracking-tight text-[var(--rsi-navy)]">
                      {category.name}
                    </div>
                    <div className="text-xs text-gray-500 font-mono-plex mt-1">/{category.slug}</div>
                    <p className="text-sm text-gray-600 mt-2 line-clamp-2">{category.description}</p>
                  </div>
                </button>
              ))}
            </div>
          )}

          {!showProductGrid ? (
            <div className="text-center py-12 text-gray-500" data-testid="products-category-prompt">
              {copy.categoryPrompt}
            </div>
          ) : isLoading ? (
            <div className="text-center py-20 text-gray-500">{copy.loading}</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20 text-gray-500" data-testid="products-empty">
              {copy.empty}
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p, i) => (
                <motion.div
                  key={p.id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: (i % 6) * 0.05 }}
                  className="rsi-card overflow-hidden group flex flex-col"
                  data-testid={`product-card-${p.slug}`}
                >
                  <Link to={`/products/${p.slug}`} className="block">
                    <div className="aspect-[4/3] overflow-hidden bg-gray-100">
                      <img
                        src={p.image_url}
                        alt={p.name}
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                    </div>
                  </Link>
                  <div className="p-5 flex flex-col flex-1">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="badge-navy">{categoryMap.get(p.category_slug)?.name || p.category_slug}</span>
                      {p.origin_country && (
                        <span className="text-xs text-gray-500">· {p.origin_country}</span>
                      )}
                    </div>
                    <Link to={`/products/${p.slug}`}>
                      <h3 className="font-display font-extrabold text-lg tracking-tight text-[var(--rsi-navy)] hover:text-[var(--rsi-purple)] transition-colors">
                        {p.name}
                      </h3>
                    </Link>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2 flex-1">
                      {p.short_description}
                    </p>
                    <div className="mt-4 flex items-center justify-between text-xs">
                      <div>
                        <div className="text-gray-400 uppercase tracking-wider text-[10px]">{copy.moq}</div>
                        <div className="font-mono-plex font-medium">{p.moq || "—"}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-gray-400 uppercase tracking-wider text-[10px]">{copy.price}</div>
                        <div className="font-mono-plex font-medium">{p.price_range || copy.onQuote}</div>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setRfqProduct(p);
                        setRfqOpen(true);
                      }}
                      className="mt-5 btn-primary w-full !py-2.5 text-sm"
                      data-testid={`rfq-button-${p.slug}`}
                    >
                      {copy.requestQuote} <ArrowUpRight className="h-4 w-4" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      <RFQDialog open={rfqOpen} onOpenChange={setRfqOpen} product={rfqProduct} />
    </>
  );
}
